import type { RxCollection, MangoQuerySelector } from 'rxdb';
import type {
    AreaDescriptionDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    ParcelDocType,
    ProjectDocType
} from '@ast-data/shared';
import { composeFileName, fileNameSpan } from '../../../utils/downloadFile';
import { parcelRows } from '../diaryPdf/exportData';
import {
    findPlacedMedia,
    resolveExportMedia,
    type ExportMedia
} from '../pdf/exportMedia';
import { areaDescriptionLocation } from '../../findings/areaDescriptionLabel';
import { compareNatural } from '../../../utils/compare';
import { showDocs } from '../../users/personLabels';
import {
    createDeliverableTexts,
    type DeliverableTexts
} from '../../translation/deliverableTexts';

// ---------------------------------------------------------------------------
/**
 * Data layer for the Flächenbeschreibung PDF export: gathers what a sheet needs
 * into a plain, RxDB-free structure the react-pdf layout
 * (AreaDescriptionPdfDocument.tsx) consumes.
 */
// ---------------------------------------------------------------------------

/** One Flächenbeschreibung = one sheet (or more, if the text runs long). */
export interface ExportAreaDescription {
    key: string;
    doc: AreaDescriptionDocType;
    /** The Maßnahme THIS record belongs to — per sheet, not per export. */
    project: ProjectDocType;
    /** The Flurstücke, one line per Gemarkung (see `parcelRows`). */
    parcels: string[];
    /** Sketches + photos of this record, createdAt-ascending. */
    media: ExportMedia[];
}

export interface AreaDescriptionExportData {
    areaDescriptions: ExportAreaDescription[];
}

/** The collections `collectAreaDescriptionExportData` reads. */
export interface AreaDescriptionExportCollections {
    projects: RxCollection<ProjectDocType>;
    areaDescriptions: RxCollection<AreaDescriptionDocType>;
    parcels: RxCollection<ParcelDocType>;
    media: RxCollection<MediaDocType>;
    mediaLinks: RxCollection<MediaLinkDocType>;
    mediaGroups: RxCollection<MediaGroupDocType>;
}

/** A measure as the sheet prints it: German decimal comma, then the unit. */
export function metresLabel(value: string): string {
    const trimmed = value.trim();
    return trimmed ? `${trimmed.replace('.', ',')} m` : '';
}

/**
 * German throughout and independent of the UI language, like the other sheets'
 * file names. The Maßnahme leads only when the document is of ONE.
 */
export function areaDescriptionPdfFileName(
    measureNumbers: readonly string[],
    docs: readonly AreaDescriptionDocType[]
): string {
    const measures = [...new Set(measureNumbers.filter(Boolean))];
    const labels = docs.map((doc) =>
        areaDescriptionLocation(doc)
            .replace(/\.\s*/g, '_')
            .replace(/,\s*/g, '-')
    );
    return composeFileName(
        [
            measures.length === 1
                ? measures[0]
                : measures.length > 1
                  ? `${measures.length}Massnahmen`
                  : '',
            'Flaechenbeschreibungen',
            ...fileNameSpan(labels, 'Flaechenbeschreibungen')
        ],
        '.pdf'
    );
}

/** A collection's matching documents, people shown by their accounts. */
async function findAllShown<T>(
    collection: RxCollection<T>,
    selector: Record<string, unknown>,
    labelOf?: (id: string) => string
): Promise<T[]> {
    const docs = await collection
        .find({ selector: selector as MangoQuerySelector<T> })
        .exec();
    return showDocs<T>(collection.name, docs, labelOf);
}

/**
 * Gather the export payload for a set of Flächenbeschreibungen, named by id —
 * the cross-project Auswertung ticks records across Maßnahmen.
 *
 * Records whose project is not in the local database are DROPPED: the sheet's
 * whole head comes from the project.
 */
export async function collectAreaDescriptionExportData({
    collections,
    ids,
    labelOf,
    deliverable = createDeliverableTexts(null, null)
}: {
    collections: AreaDescriptionExportCollections;
    ids: readonly string[];
    /** Shows linked people by their accounts — see usePersonLabels. */
    labelOf?: (id: string) => string;
    /** Puts the accepted German translations in; without, the originals. */
    deliverable?: DeliverableTexts;
}): Promise<AreaDescriptionExportData> {
    const findAll = <T>(
        collection: RxCollection<T>,
        selector: Record<string, unknown>
    ) => findAllShown<T>(collection, selector, labelOf);
    if (ids.length === 0) {
        return { areaDescriptions: [] };
    }
    const docs = await deliverable.apply(
        'areaDescription',
        await findAll<AreaDescriptionDocType>(collections.areaDescriptions, {
            id: { $in: [...ids] }
        })
    );
    if (docs.length === 0) {
        return { areaDescriptions: [] };
    }

    const allProjects = await findAll<ProjectDocType>(collections.projects, {});
    const projectById = new Map(
        allProjects.map((project) => [project.id, project])
    );

    // Maßnahme first, then Fläche and Planum: one chapter per Maßnahme.
    const printable = docs
        .filter((doc) => projectById.has(doc.projectId))
        .sort(
            (firstDoc, secondDoc) =>
                compareNatural(
                    projectById.get(firstDoc.projectId)?.measureNumber ?? '',
                    projectById.get(secondDoc.projectId)?.measureNumber ?? ''
                ) ||
                compareNatural(firstDoc.area, secondDoc.area) ||
                compareNatural(firstDoc.planum, secondDoc.planum) ||
                compareNatural(firstDoc.sondage, secondDoc.sondage)
        );
    if (printable.length === 0) {
        return { areaDescriptions: [] };
    }

    const parcelIds = [...new Set(printable.flatMap((doc) => doc.parcelIds))];
    const parcels =
        parcelIds.length > 0
            ? await findAll<ParcelDocType>(collections.parcels, {
                  id: { $in: parcelIds }
              })
            : [];
    const parcelById = new Map(parcels.map((parcel) => [parcel.id, parcel]));

    const mediaFor = await resolveExportMedia(
        await findPlacedMedia(
            collections.media,
            collections.mediaLinks,
            collections.mediaGroups,
            printable.map((doc) => doc.id)
        )
    );

    return {
        areaDescriptions: printable.map((doc) => ({
            key: doc.id,
            doc,
            // Present — `printable` dropped every record whose project is not.
            project: projectById.get(doc.projectId) as ProjectDocType,
            parcels: parcelRows(
                doc.parcelIds
                    .map((id) => parcelById.get(id))
                    .filter((parcel): parcel is ParcelDocType => !!parcel)
            ),
            media: mediaFor(doc.id)
        }))
    };
}
